import { numericFormatter } from "react-number-format";
import {
  BudgetAssumptions,
  Currency,
  TimeUnit,
} from "components/dashboard/body/projects/panels/budget/BudgetAssumptionsTypes";
import type { ProductionLocationValue } from "utils/resolveBudgetDisplayLocation";

export type BudgetAssumptionsFormValues = Omit<
  BudgetAssumptions,
  "max_budget" | "prep" | "shoot" | "post" | "wrap"
> & {
  max_budget: { currency: Currency.USD; value: string };
  prep: { unit: TimeUnit; value?: number };
  shoot: { unit: TimeUnit; value?: number } | null;
  post: { unit: TimeUnit; value?: number };
  wrap: { unit: TimeUnit; value?: number };
};

export interface SeedBudgetAssumptionsInput {
  lockedAccountsTotal: number;
  currentGridTotal: number;
  rerunParams?: BudgetAssumptions | null;
  versionProductionLocation?: ProductionLocationValue | null;
}

function formatMaxBudgetValue(value: number): string {
  return numericFormatter(value.toFixed(), {
    thousandSeparator: true,
    decimalScale: 0,
    allowNegative: false,
  });
}

const defaultPhase = (unit: TimeUnit = TimeUnit.WEEK) => ({ unit });

const defaultGuilds = {
  dga: false,
  wga: false,
  sag: false,
  iatse: false,
  teamsters: false,
};

export function createDefaultBudgetAssumptionsFormValues(
  input: SeedBudgetAssumptionsInput
): BudgetAssumptionsFormValues {
  const maxBudgetValue =
    input.rerunParams?.max_budget?.value ??
    (input.currentGridTotal > input.lockedAccountsTotal
      ? input.currentGridTotal
      : input.lockedAccountsTotal + 1);

  return {
    max_budget: {
      currency: Currency.USD,
      value: formatMaxBudgetValue(maxBudgetValue),
    },
    prep: input.rerunParams?.prep ?? defaultPhase(),
    // Stripboard shoot time replaces the manual shoot length, so keep it null. A `{ unit }`
    // placeholder would fail validation on the Shoot field, which the dialog hides in that mode.
    shoot: input.rerunParams?.use_stripboard_shoot_time
      ? null
      : (input.rerunParams?.shoot ?? defaultPhase()),
    post: input.rerunParams?.post ?? defaultPhase(),
    wrap: input.rerunParams?.wrap ?? defaultPhase(),
    use_stripboard_locations: input.rerunParams?.use_stripboard_locations ?? false,
    use_stripboard_shoot_time: input.rerunParams?.use_stripboard_shoot_time ?? false,
    location: input.rerunParams?.location ?? input.versionProductionLocation ?? undefined,
    guilds: input.rerunParams?.guilds ?? defaultGuilds,
  };
}

export function rerunParamsToFormValues(
  rerunParams: BudgetAssumptions,
  lockedAccountsTotal: number,
  currentGridTotal: number
): BudgetAssumptionsFormValues {
  return createDefaultBudgetAssumptionsFormValues({
    lockedAccountsTotal,
    currentGridTotal,
    rerunParams,
  });
}

export function formValuesToBudgetAssumptions(
  values: BudgetAssumptionsFormValues
): BudgetAssumptions {
  const maxBudgetNumeric = parseInt(values.max_budget.value.replace(/,/g, ""), 10);

  const mapPhase = (phase: { unit: TimeUnit; value?: number }) => ({
    unit: phase.unit,
    value: phase.value ?? 0,
  });

  const budgetAssumptions: BudgetAssumptions = {
    max_budget: {
      currency: Currency.USD,
      value: maxBudgetNumeric,
    },
    prep: mapPhase(values.prep),
    shoot: values.shoot ? mapPhase(values.shoot) : null,
    post: mapPhase(values.post),
    wrap: mapPhase(values.wrap),
    use_stripboard_locations: values.use_stripboard_locations,
    use_stripboard_shoot_time: values.use_stripboard_shoot_time,
    location: values.location,
    guilds: values.guilds,
  };

  if (budgetAssumptions.use_stripboard_locations) {
    budgetAssumptions.location = undefined;
  }

  if (budgetAssumptions.use_stripboard_shoot_time) {
    budgetAssumptions.shoot = null;
  }

  const guilds = budgetAssumptions.guilds;
  const hasAnyGuild =
    guilds && (guilds.dga || guilds.wga || guilds.sag || guilds.iatse || guilds.teamsters);
  if (!hasAnyGuild) {
    budgetAssumptions.guilds = undefined;
  }

  return budgetAssumptions;
}

export function parseVersionProductionLocation(raw: unknown): ProductionLocationValue | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const record = raw as Record<string, unknown>;
  const name = typeof record.name === "string" ? record.name.trim() : "";
  if (!name) return null;
  const placeId =
    typeof record.place_id === "string" && record.place_id.trim().length > 0
      ? record.place_id.trim()
      : null;
  return { name, place_id: placeId };
}
